import ISSUE_VIEWER_VIEW_QUERY from '@github-ui/issue-viewer/IssueViewerViewQuery.graphql'
import {registerNavigatorApp} from '@github-ui/react-core/register-app'
import {relayEnvironmentWithMissingFieldHandlerForNode} from '@github-ui/relay-environment'
import {DEFAULT_RELAY_CACHE_TTL} from '@github-ui/relay-query-cache/helpers'
import {relayRoute} from '@github-ui/relay-route'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {useEffect} from 'react'

import {App} from './App'
import {
  IssueIndexPage,
  IssueViewsPage,
  RepositoryMilestonePage,
  RepositorySavedViewPage,
  RepositoryViewNewPage,
} from './lazy-routes'
import ISSUE_INDEX_PAGE_QUERY from './pages/__generated__/IssueIndexPageQuery.graphql'
import REPOSITORY_MILESTONE_PAGE_QUERY from './pages/__generated__/RepositoryMilestonePageQuery.graphql'
import {clientSideRelayDataGenerator, setRecordMap} from './pages/ClientSideRelayDataGenerator'
import {IssueShowPage} from './pages/IssueShowPage'
import {VARIABLE_TRANSFORMERS} from './utils/variable-transformers'

const isDevelopment = () => process.env.NODE_ENV === 'development'

// Prevent `/:owner/:repo/issues/:number` from treating "new" as an issue number when navigating
// from the Relay app. A full page load lets the server hand the route to the Data Router app.
function HardNavigate() {
  useEffect(() => {
    ssrSafeWindow?.location.assign(ssrSafeWindow.location.href)
  }, [])
  return null
}

const ISSUES_INDEX_CACHE_TTL = 1000 * 60 * 60 * 24 // 24 hours in milliseconds

registerNavigatorApp('issues-react', () => {
  const relayEnvironment = relayEnvironmentWithMissingFieldHandlerForNode()
  setRecordMap(relayEnvironment)
  clientSideRelayDataGenerator({environment: relayEnvironment})

  const relayRouteConfig = {
    fallback: isDevelopment() ? 'Loading...' : '',
    relayEnvironment,
  }
  return {
    App,
    routes: [
      relayRoute({
        ...relayRouteConfig,
        path: '/:owner/:name/issues/new',
        resourceName: 'IssueRepoNew',
        title: 'New Issue',
        Component: HardNavigate,
        queryConfigs: {},
      }),
      relayRoute({
        ...relayRouteConfig,
        path: '/:owner/:name/issues/new/choose',
        resourceName: 'IssueRepoNewChoose',
        title: 'Choose an Issue Template',
        Component: HardNavigate,
        queryConfigs: {},
      }),
      relayRoute({
        ...relayRouteConfig,
        path: '/:owner/:repo/issues/views/new',
        resourceName: 'RepositoryViewNew',
        title: 'New view · :owner/:repo',
        Component: RepositoryViewNewPage,
        queryConfigs: {},
      }),
      relayRoute({
        ...relayRouteConfig,
        path: '/:owner/:repo/issues/views/:id',
        resourceName: 'RepositorySavedView',
        title: 'View · :owner/:repo',
        Component: RepositorySavedViewPage,
        queryConfigs: {},
      }),
      relayRoute({
        ...relayRouteConfig,
        path: '/:owner/:repo/issues/views',
        resourceName: 'IssueViews',
        title: 'Views · :owner/:repo',
        Component: IssueViewsPage,
        queryConfigs: {},
      }),
      relayRoute({
        ...relayRouteConfig,
        path: '/:owner/:repo/issues/:number',
        resourceName: 'IssueShow',
        // The issue viewer manages its own document title (from the issue's title), so this route
        // intentionally provides an empty title to opt out of the generic title manager.
        title: '',
        Component: IssueShowPage,
        transformVariables: VARIABLE_TRANSFORMERS['/:owner/:repo/issues/:number'],
        queryConfigs: {
          issueViewerViewQuery: {
            concreteRequest: ISSUE_VIEWER_VIEW_QUERY,
            variableMappers: routeParams => {
              return {
                owner: routeParams.pathParams['owner'],
                repo: routeParams.pathParams['repo'],
                number: routeParams.pathParams['number'] ? parseInt(routeParams.pathParams['number']) : undefined,
              }
            },
          },
        },
        maxAge: 10,
        cacheTTL: DEFAULT_RELAY_CACHE_TTL,
        exclude_from_cache_key: ['allowedOwner'],
      }),
      relayRoute({
        ...relayRouteConfig,
        path: '/:owner/:repo/issues',
        resourceName: 'IssueRepoIndex',
        title: 'Issues · :owner/:repo',
        Component: IssueIndexPage,
        transformVariables: VARIABLE_TRANSFORMERS['/:owner/:repo/issues'],
        queryConfigs: {
          pageQuery: {
            concreteRequest: ISSUE_INDEX_PAGE_QUERY,
          },
        },
        cacheTTL: ISSUES_INDEX_CACHE_TTL,
        maxAge: 10,
        exclude_from_cache_key: ['includeReactions'],
      }),
      relayRoute({
        ...relayRouteConfig,
        path: '/:owner/:repo/issues/created_by/:author',
        resourceName: 'IssueRepoIndex',
        title: 'Issues · :owner/:repo',
        Component: IssueIndexPage,
        transformVariables: VARIABLE_TRANSFORMERS['/:owner/:repo/issues/created_by/:author'],
        queryConfigs: {
          pageQuery: {
            concreteRequest: ISSUE_INDEX_PAGE_QUERY,
          },
        },
      }),
      relayRoute({
        ...relayRouteConfig,
        path: '/:owner/:repo/issues/created_by/app/:author',
        resourceName: 'IssueRepoIndex',
        title: 'Issues · :owner/:repo',
        Component: IssueIndexPage,
        transformVariables: VARIABLE_TRANSFORMERS['/:owner/:repo/issues/created_by/app/:author'],
        queryConfigs: {
          pageQuery: {
            concreteRequest: ISSUE_INDEX_PAGE_QUERY,
          },
        },
      }),

      relayRoute({
        ...relayRouteConfig,
        path: '/:owner/:repo/issues/assigned/:assignee',
        resourceName: 'IssueRepoIndex',
        title: 'Issues · :owner/:repo',
        Component: IssueIndexPage,
        transformVariables: VARIABLE_TRANSFORMERS['/:owner/:repo/issues/assigned/:assignee'],
        queryConfigs: {
          pageQuery: {
            concreteRequest: ISSUE_INDEX_PAGE_QUERY,
          },
        },
      }),

      relayRoute({
        ...relayRouteConfig,
        path: '/:owner/:repo/milestone/:number',
        resourceName: 'RepoMilestoneShow',
        title: '', // Milestone-based title is set in the component; This should be empty otherwise NavigatorClientEntry.tsx undoes the dynamic title.
        // Document title will never be empty in practice since we have the Github fallback should the component not set it.
        Component: RepositoryMilestonePage,
        transformVariables: VARIABLE_TRANSFORMERS['/:owner/:repo/milestone/:number'],
        queryConfigs: {
          pageQuery: {
            concreteRequest: REPOSITORY_MILESTONE_PAGE_QUERY,
          },
        },
      }),
      // Milestone index, new, and edit routes are handled by the Data Router `issues` app.
      relayRoute({
        ...relayRouteConfig,
        path: '/:owner/:repo/issues/mentioned/:mentioned',
        resourceName: 'IssueRepoIndex',
        title: 'Issues · :owner/:repo',
        Component: IssueIndexPage,
        transformVariables: VARIABLE_TRANSFORMERS['/:owner/:repo/issues/mentioned/:mentioned'],
        queryConfigs: {
          pageQuery: {
            concreteRequest: ISSUE_INDEX_PAGE_QUERY,
          },
        },
      }),
      relayRoute({
        ...relayRouteConfig,
        path: '/:owner/:repo/labels/:label',
        transformVariables: VARIABLE_TRANSFORMERS['/:owner/:repo/labels/:label'],
        resourceName: 'IssueLabelIndex',
        title: 'Labels · :owner/:repo',
        Component: IssueIndexPage,
        queryConfigs: {
          pageQuery: {
            concreteRequest: ISSUE_INDEX_PAGE_QUERY,
          },
        },
      }),
    ],
  }
})
